// "Bericht" tab body — per-provider cost breakdown for the current month
// (owner ask: "a report where users see cost per provider"). Flat data
// surface (DESIGN.md §14) — lives inside BotPanel/BotCompareDock's glass
// chrome, so nothing here reaches for GlassCard. A plain <table> is used for
// the rows: shared/ui has no table primitive as of this write (checked
// index.ts), and DESIGN.md §14's flat pattern (bg-surface-container-lowest +
// border-outline-variant) covers dense repeating data like this either way.

import { useTranslation } from 'react-i18next'
import { EmptyState, Skeleton } from '@/shared/ui'
import { useUsageReport } from './hooks/useBot'
import { formatEur, formatTokens } from './botUtils'
import type { BotUsageProviderRow, BotUsageUserRow } from './api/botApi'

interface ProviderTableProps {
  rows: BotUsageProviderRow[]
}

/** One provider-breakdown table with a "Gesamt" footer row. Reused for both
 *  the org-wide (`data.org`) and caller-only (`data.mine`) sections below —
 *  same shape, different WHERE clause server-side (see bot.js's
 *  bot-usage-report-get). */
function ProviderTable({ rows }: ProviderTableProps) {
  const { t } = useTranslation('bot')
  const totals = rows.reduce(
    (acc, r) => ({
      answers: acc.answers + r.answers,
      tokens: acc.tokens + r.tokensIn + r.tokensOut,
      costMicroEur: acc.costMicroEur + r.costMicroEur,
    }),
    { answers: 0, tokens: 0, costMicroEur: 0 },
  )
  return (
    <div className="overflow-x-auto rounded-xl border border-outline-variant bg-surface-container-lowest">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-outline-variant text-xs text-on-surface-variant">
            <th className="text-left font-medium px-3 py-2">{t('report.columns.provider')}</th>
            <th className="text-right font-medium px-3 py-2 tabular-nums">{t('report.columns.answers')}</th>
            <th className="text-right font-medium px-3 py-2 tabular-nums">{t('report.columns.tokens')}</th>
            <th className="text-right font-medium px-3 py-2 tabular-nums">{t('report.columns.cost')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.provider} className="border-b border-outline-variant last:border-0">
              <td className="px-3 py-2 text-on-surface">{t(`report.providers.${row.provider}`)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-on-surface">{formatTokens(row.answers)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-on-surface">{formatTokens(row.tokensIn + row.tokensOut)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-on-surface">{formatEur(row.costMicroEur / 1_000_000, 2)} €</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-outline-variant font-semibold">
            <td className="px-3 py-2 text-on-surface">{t('report.total')}</td>
            <td className="px-3 py-2 text-right tabular-nums text-on-surface">{formatTokens(totals.answers)}</td>
            <td className="px-3 py-2 text-right tabular-nums text-on-surface">{formatTokens(totals.tokens)}</td>
            <td className="px-3 py-2 text-right tabular-nums text-on-surface">{formatEur(totals.costMicroEur / 1_000_000, 2)} €</td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

/** Per-user breakdown (rendered only when the backend sent `users`, i.e. the
 *  caller is IT.Admin). Same flat table treatment as ProviderTable; the
 *  UPN's local part is enough to recognize a colleague — the full UPN sits
 *  in the title attribute. */
function UserTable({ rows }: { rows: BotUsageUserRow[] }) {
  const { t } = useTranslation('bot')
  const totals = rows.reduce(
    (acc, r) => ({
      answers: acc.answers + r.answers,
      tokens: acc.tokens + r.tokensIn + r.tokensOut,
      costMicroEur: acc.costMicroEur + r.costMicroEur,
    }),
    { answers: 0, tokens: 0, costMicroEur: 0 },
  )
  return (
    <div className="overflow-x-auto rounded-xl border border-outline-variant bg-surface-container-lowest">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-outline-variant text-xs text-on-surface-variant">
            <th className="text-left font-medium px-3 py-2">{t('report.columns.user')}</th>
            <th className="text-right font-medium px-3 py-2 tabular-nums">{t('report.columns.answers')}</th>
            <th className="text-right font-medium px-3 py-2 tabular-nums">{t('report.columns.tokens')}</th>
            <th className="text-right font-medium px-3 py-2 tabular-nums">{t('report.columns.cost')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.user} className="border-b border-outline-variant last:border-0">
              <td className="px-3 py-2 text-on-surface truncate max-w-[160px]" title={row.user}>{row.user.split('@')[0]}</td>
              <td className="px-3 py-2 text-right tabular-nums text-on-surface">{formatTokens(row.answers)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-on-surface">{formatTokens(row.tokensIn + row.tokensOut)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-on-surface">{formatEur(row.costMicroEur / 1_000_000, 2)} €</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-outline-variant font-semibold">
            <td className="px-3 py-2 text-on-surface">{t('report.total')}</td>
            <td className="px-3 py-2 text-right tabular-nums text-on-surface">{formatTokens(totals.answers)}</td>
            <td className="px-3 py-2 text-right tabular-nums text-on-surface">{formatTokens(totals.tokens)}</td>
            <td className="px-3 py-2 text-right tabular-nums text-on-surface">{formatEur(totals.costMicroEur / 1_000_000, 2)} €</td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

export function ReportTab() {
  const { t } = useTranslation('bot')
  const { data, isLoading, isError } = useUsageReport()

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    )
  }

  if (isError || !data) {
    return <p role="alert" className="text-xs text-error">{t('errors.generic')}</p>
  }

  // Any unpriced answer in either scope is enough to show the note once —
  // no need to tell the user twice which table it came from.
  const hasUnknown = [...data.org, ...data.mine].some((row) => row.unknownCosts > 0)

  return (
    <div className="space-y-5">
      <p className="text-xs text-on-surface-variant">{t('report.subtitle')}</p>

      {data.org.length === 0 ? (
        <EmptyState icon="receipt_long" title={t('report.empty.title')} description={t('report.empty.description')} />
      ) : (
        <ProviderTable rows={data.org} />
      )}

      <div className="space-y-1.5">
        <span className="block text-xs font-medium text-on-surface">{t('report.mineTitle')}</span>
        {data.mine.length === 0 ? (
          <p className="text-xs text-on-surface-variant">{t('report.mineEmpty')}</p>
        ) : (
          <ProviderTable rows={data.mine} />
        )}
      </div>

      {data.users && data.users.length > 0 && (
        <div className="space-y-1.5">
          <span className="block text-xs font-medium text-on-surface">{t('report.usersTitle')}</span>
          <UserTable rows={data.users} />
        </div>
      )}

      {hasUnknown && <p className="text-xs text-on-surface-variant">{t('report.unknownNote')}</p>}
    </div>
  )
}

export default ReportTab
